import axios from "axios";
import config from "./../config";

// withCredentials is set globally in authHelper so the HttpOnly session cookie
// is sent on every request. No token is read from localStorage here.
const getHeaders = (multiFormData = false) => ({
  "Content-Type": multiFormData ? "multipart/form-data" : "application/json",
});

const userCreate = async (end_Point, params, multiFormData = false) =>
  axios.post(`${config.BASE_URL}/${end_Point}`, params, {
    headers: getHeaders(multiFormData),
  });

const userUpdate = async (end_Point, params, id, multiFormData = false) =>
  axios.put(`${config.BASE_URL}/${end_Point}/${id}`, params, {
    headers: getHeaders(multiFormData),
  });

const addDeliveryAddress = async (end_Point, params, multiFormData = false) =>
  axios.post(`${config.BASE_URL}/${end_Point}`, params, {
    headers: getHeaders(multiFormData),
  });

const addCpAddress = async (end_Point, params, multiFormData = false) =>
  axios.post(`${config.BASE_URL}/${end_Point}`, params, {
    headers: getHeaders(multiFormData),
  });

const orderCreation = async (end_Point, params, multiFormData = false) =>
  axios.post(`${config.BASE_URL}/${end_Point}`, params, {
    headers: getHeaders(multiFormData),
  });

const orderUpdate = async (end_Point, id, params, multiFormData = false) =>
  axios.put(`${config.BASE_URL}/${end_Point}/${id}`, params, {
    headers: getHeaders(multiFormData),
  });

const userFind = async (end_point, signal = null) =>
  axios.get(`${config.BASE_URL}/${end_point}`, {
    headers: getHeaders(),
    signal,
  });

const cpAddress = async (end_point) =>
  axios.get(`${config.BASE_URL}/${end_point}`, { headers: getHeaders() });

const getPromos = async (end_point) =>
  axios.get(`${config.BASE_URL}/${end_point}`, { headers: getHeaders() });

const getTransactionsData = async (end_point, params, id = null) => {
  if (id) {
    return axios.get(`${config.BASE_URL}/${end_point}/${id}`, {
      headers: getHeaders(),
    });
  }
  return axios.get(`${config.BASE_URL}/${end_point}`, {
    params,
    headers: getHeaders(),
  });
};

const updateTransactionsData = async (end_point, params, id) =>
  axios.put(`${config.BASE_URL}/${end_point}/${id}`, params, {
    headers: getHeaders(),
  });

const updateTransactionOrders = async (end_point, params) =>
  axios.post(`${config.BASE_URL}/${end_point}`, params, {
    headers: getHeaders(),
  });

const updatePrinterSettings = async (end_point, params) =>
  axios.put(`${config.BASE_URL}/${end_point}`, params, {
    headers: getHeaders(),
  });

const getPrinterSettings = async (end_point) =>
  axios.get(`${config.BASE_URL}/${end_point}`, { headers: getHeaders() });

// Per-shop POS product tile order. GET returns { product_layout: {...} };
// PUT merges the partial document per category (an empty list removes one).
// A 404 here means the backend route is not deployed yet — layoutSlice falls
// back to localStorage in that case.
const getProductLayout = async () =>
  axios.get(`${config.BASE_URL}/shop/product_layout`, { headers: getHeaders() });

const updateProductLayout = async (partialLayout) =>
  axios.put(
    `${config.BASE_URL}/shop/product_layout`,
    { product_layout: partialLayout },
    { headers: getHeaders() }
  );

// Viva cloud-terminal payments. These live at the /api/v1 root (NOT under
// shop/) — the backend infers the "pos" actor and resolves the shop's
// viva_terminal_id itself. The POS only ever sends order_id.
const createTerminalPayment = async (orderId) =>
  axios.post(
    `${config.BASE_URL}/terminal_payments`,
    { order_id: orderId },
    { headers: getHeaders() }
  );

// ONE Viva session for several orders (combined hand-back). The backend sums
// the orders' amounts itself, arms a single sale and settles every member on
// the webhook. A one-order list behaves exactly like createTerminalPayment.
const createGroupTerminalPayment = async (orderIds) =>
  axios.post(
    `${config.BASE_URL}/terminal_payments/group`,
    { order_ids: orderIds },
    { headers: getHeaders() }
  );

const getTerminalPayment = async (sessionId) =>
  axios.get(`${config.BASE_URL}/terminal_payments/${sessionId}`, {
    headers: getHeaders(),
  });

// The backend calls Viva synchronously inside this request; bound the wait
// so a hung device call can't freeze the Cancel button. On timeout the modal
// falls back to the failure phase (same as any non-busy abort error).
const ABORT_TIMEOUT_MS = 15000;
const abortTerminalPayment = async (sessionId) =>
  axios.post(
    `${config.BASE_URL}/terminal_payments/${sessionId}/abort`,
    {},
    { headers: getHeaders(), timeout: ABORT_TIMEOUT_MS }
  );

// Account-claim link for a customer the laundry created ("Send app invitation").
// Always channel "qr": the POS shows the link as a QR code and nothing is sent
// anywhere — on the backend the channel only sets how long the link lives.
// The customer has a single live link shared by the QR and the emails, so
// regenerate: true also kills the link already sitting in their inbox.
// 409 { error: "already_claimed" } means the customer already has app access.
const issueClaimToken = async (userId, { regenerate = false } = {}) =>
  axios.post(
    `${config.BASE_URL}/shop/claim_tokens`,
    { user_id: userId, channel: "qr", regenerate },
    { headers: getHeaders() }
  );

const ApiRequest = {
  issueClaimToken,
  userCreate,
  userUpdate,
  userFind,
  addDeliveryAddress,
  cpAddress,
  addCpAddress,
  orderCreation,
  orderUpdate,
  getPromos,
  getTransactionsData,
  updateTransactionsData,
  updateTransactionOrders,
  updatePrinterSettings,
  getPrinterSettings,
  getProductLayout,
  updateProductLayout,
  createTerminalPayment,
  createGroupTerminalPayment,
  getTerminalPayment,
  abortTerminalPayment,
};

export default ApiRequest;
